import React from 'react';
import { motion } from 'framer-motion';
import {
    FiClock,
    FiCalendar,
    FiStar,
    FiHome,
    FiMapPin,
    FiCloud,
    FiMoon,
    FiAlertCircle,
    FiZap,
    FiCheck
} from 'react-icons/fi';

interface ExperienceStepProps {
    data: {
        workFrequency?: 'daily' | 'weekly' | 'occasional' | 'first-time';
        environment?: 'indoor' | 'outdoor' | 'both';
        toolFamiliarity?: string[];
        nightWork?: boolean;
        emergencyWork?: boolean;
        shortNotice?: boolean;
    };
    onUpdate: (data: any) => void;
    onNext: () => void;
    onBack: () => void;
}

const ExperienceStep: React.FC<ExperienceStepProps> = ({ data, onUpdate, onNext, onBack }) => {
    const handleFrequencySelect = (frequency: 'daily' | 'weekly' | 'occasional' | 'first-time') => {
        onUpdate({ ...data, workFrequency: frequency });
    };

    const handleEnvironmentSelect = (env: 'indoor' | 'outdoor' | 'both') => {
        onUpdate({ ...data, environment: env });
    };

    const handleToggle = (field: 'nightWork' | 'emergencyWork' | 'shortNotice') => {
        onUpdate({ ...data, [field]: !data[field] });
    };

    const canProceed = data.workFrequency && data.environment;

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-white mb-2 font-serif">
                    Tell Us About Your Experience
                </h2>
                <p className="text-white/60">
                    Help us understand your work patterns and preferences
                </p>
            </div>

            {/* Work Frequency */}
            <div className="space-y-3">
                <h3 className="text-xs font-bold text-premium-gold uppercase tracking-widest">
                    How often do you do this kind of work?
                </h3>
                <div className="grid grid-cols-2 gap-3">
                    {[
                        { value: 'daily', label: 'Every Day', desc: 'It\'s what I do regularly', icon: FiZap, color: '#D4AF37' },
                        { value: 'weekly', label: 'Weekly', desc: 'A few times per week/month', icon: FiCalendar, color: '#D4AF37' },
                        { value: 'occasional', label: 'Sometimes', desc: 'When opportunities come', icon: FiClock, color: '#D4AF37' },
                        { value: 'first-time', label: 'New to This', desc: 'I\'m just getting started', icon: FiStar, color: '#D4AF37' }
                    ].map((option) => (
                        <motion.button
                            key={option.value}
                            onClick={() => handleFrequencySelect(option.value as any)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`p-4 rounded-xl border-2 text-left transition-all flex items-start gap-4 ${data.workFrequency === option.value
                                ? 'border-premium-gold bg-premium-gold/10'
                                : 'border-white/10 bg-white/5 hover:border-white/20'
                                }`}
                        >
                            <div className={`p-3 rounded-lg bg-white/5 ${data.workFrequency === option.value ? 'text-premium-gold' : 'text-white/40'}`}>
                                <option.icon size={24} />
                            </div>
                            <div>
                                <div className="font-bold text-white text-sm mb-1">{option.label}</div>
                                <div className="text-[11px] text-white/50 leading-tight">{option.desc}</div>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>

            {/* Environment Preference */}
            <div className="space-y-3">
                <h3 className="text-xs font-bold text-premium-gold uppercase tracking-widest">
                    Where do you prefer to work?
                </h3>
                <div className="grid grid-cols-3 gap-3">
                    {[
                        { value: 'indoor', label: 'Indoors', icon: FiHome, desc: 'Office, home' },
                        { value: 'outdoor', label: 'Outdoors', icon: FiMapPin, desc: 'Outside' },
                        { value: 'both', label: 'Both', icon: FiCloud, desc: 'Either' }
                    ].map((option) => (
                        <motion.button
                            key={option.value}
                            onClick={() => handleEnvironmentSelect(option.value as any)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className={`p-4 rounded-xl border-2 text-center transition-all ${data.environment === option.value
                                ? 'border-premium-gold bg-premium-gold/10'
                                : 'border-white/10 bg-white/5 hover:border-white/20'
                                }`}
                        >
                            <div className={`flex justify-center mb-3 ${data.environment === option.value ? 'text-premium-gold' : 'text-white/30'}`}>
                                <option.icon size={28} />
                            </div>
                            <div className="font-bold text-white text-xs mb-1 uppercase tracking-wider">{option.label}</div>
                            <div className="text-[10px] text-white/40">{option.desc}</div>
                        </motion.button>
                    ))}
                </div>
            </div>

            {/* Availability Preferences */}
            <div className="space-y-3">
                <h3 className="text-xs font-bold text-premium-gold uppercase tracking-widest">
                    Availability Preferences (Optional)
                </h3>
                <div className="space-y-2">
                    {[
                        { field: 'nightWork', label: 'I can work nights', icon: FiMoon },
                        { field: 'emergencyWork', label: 'I can do emergency work', icon: FiAlertCircle },
                        { field: 'shortNotice', label: 'I can work on short notice', icon: FiZap }
                    ].map((option) => (
                        <motion.button
                            key={option.field}
                            onClick={() => handleToggle(option.field as any)}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            className={`w-full p-4 rounded-xl border-2 text-left transition-all ${data[option.field as keyof typeof data]
                                ? 'border-green-500/50 bg-green-500/10'
                                : 'border-white/10 bg-white/5 hover:border-white/20'
                                }`}
                        >
                            <div className="flex items-center gap-4">
                                <div className={`p-2 rounded-lg bg-white/5 ${data[option.field as keyof typeof data] ? 'text-green-400' : 'text-white/20'}`}>
                                    <option.icon size={20} />
                                </div>
                                <div className="flex-1">
                                    <div className="font-medium text-sm text-white">{option.label}</div>
                                </div>
                                <div
                                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${data[option.field as keyof typeof data]
                                        ? 'bg-green-500 border-green-500'
                                        : 'border-white/20'
                                        }`}
                                >
                                    {data[option.field as keyof typeof data] && (
                                        <FiCheck size={12} className="text-white" />
                                    )}
                                </div>
                            </div>
                        </motion.button>
                    ))}
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-4">
                <button
                    onClick={onBack}
                    className="px-6 py-2 text-white/70 hover:text-white transition-colors"
                >
                    ← Back
                </button>
                <button
                    onClick={onNext}
                    disabled={!canProceed}
                    className={`px-8 py-3 rounded-lg font-bold transition-all ${canProceed
                        ? 'bg-gradient-to-r from-premium-gold-dark to-premium-gold text-[#1a1510] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transform hover:scale-105'
                        : 'bg-white/10 text-white/40 cursor-not-allowed'
                        }`}
                >
                    Continue to Review →
                </button>
            </div>
        </div>
    );
};

export default ExperienceStep;
